import { useCallback, useState } from 'react';
import type { RoomFile } from '@shared/protocol';

export interface Upload {
  id: string;
  name: string;
  loaded: number;
  total: number;
  error?: string;
}

/**
 * File uploads into a room, one raw PUT per file.
 *
 * XHR rather than fetch because fetch still cannot report upload progress,
 * and with no size limit a large file needs a bar, not a spinner. Finished
 * uploads drop out of this list on their own: the server broadcasts the new
 * file list to the whole room, which is what the panel renders.
 */
export function useUploads(onError: (message: string) => void) {
  const [uploads, setUploads] = useState<Upload[]>([]);

  const patch = useCallback((id: string, change: Partial<Upload>) => {
    setUploads((list) => list.map((u) => (u.id === id ? { ...u, ...change } : u)));
  }, []);

  const remove = useCallback((id: string) => {
    setUploads((list) => list.filter((u) => u.id !== id));
  }, []);

  /**
   * @param onDone  called per file with what the server stored -- its final
   *                name can differ from the one sent -- and whether the
   *                server kept an identical file instead of storing a copy
   */
  const upload = useCallback(
    (roomId: string, files: File[], onDone?: (file: RoomFile, reused: boolean) => void) => {
      for (const file of files) {
        const id = crypto.randomUUID();
        setUploads((list) => [...list, { id, name: file.name, loaded: 0, total: file.size }]);

        const xhr = new XMLHttpRequest();
        xhr.open(
          'PUT',
          `/api/rooms/${encodeURIComponent(roomId)}/files/${encodeURIComponent(file.name)}`,
        );
        // Always octet-stream: the server must never mistake a .json upload
        // for a JSON request body.
        xhr.setRequestHeader('Content-Type', 'application/octet-stream');
        xhr.upload.onprogress = (e) => {
          if (e.lengthComputable) patch(id, { loaded: e.loaded, total: e.total });
        };
        xhr.onload = () => {
          if (xhr.status >= 200 && xhr.status < 300) {
            remove(id);
            try {
              const body = JSON.parse(xhr.responseText) as { file: RoomFile; reused?: boolean };
              onDone?.(body.file, body.reused === true);
            } catch {
              /* stored, but we cannot say where; the list still updates */
            }
            return;
          }
          let message = `Upload failed (${xhr.status}).`;
          try {
            message = (JSON.parse(xhr.responseText) as { error?: string }).error ?? message;
          } catch {
            /* not JSON */
          }
          patch(id, { error: message });
          onError(`${file.name}: ${message}`);
        };
        xhr.onerror = () => {
          patch(id, { error: 'Connection lost.' });
          onError(`${file.name}: connection lost during upload.`);
        };
        xhr.send(file);
      }
    },
    [patch, remove, onError],
  );

  return { uploads, upload, dismiss: remove };
}
